import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import {
  LinkBtn,
  LinkWrapper,
} from 'app/pages/library/videos/videoList/components/SendAndShare';

import { MdClose } from 'react-icons/md';
import { OrganizationVideosTable } from './OrganizationVideosTable';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import { useAuth } from 'lib/context';
import { Button } from 'react-covideo-common';
import { ModalOrganizationVideoForm } from './ModalOrganizationVideoForm';
import { Flex } from 'lib/components/styles/layout';
import { ModalOrganizationDuplicatePlaylist } from './playlist/ModalOrganizationDuplicatePlaylist';
import {
  OrganizationVideoSortKey,
  useOrganizationVideosQuery,
} from 'lib/api/organizationVideo/useOrganizationVideoQuery';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { useOrganizationVideoFiltersQuery } from 'lib/api/organizationVideo/useOrganizationVideoFiltersQuery';

const Title = styled.span`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  font-feature-settings:
    'tnum' on,
    'lnum' on,
    'ss02' on;
  color: ${theme.palette.covideoBlue100};
`;

type Props = {
  handleModalClose: () => void;
  onCopy?: () => void;
};

export const DEFAULT_PAGE = 0;
export const DEFAULT_SIZE = 10;

export const ModalOrganizationVideos = ({
  handleModalClose,
  onCopy,
}: Props) => {
  const {
    userData: { isOrganizationAdmin },
  } = useAuth();

  // state
  const [showOrganizationVideoFormModal, setShowOrganizationVideoFormModal] =
    useState(false);
  const [
    showOrganizationDuplicatePlaylistModal,
    setShowOrganizationDuplicatePlaylistModal,
  ] = useState(false);
  const [filter, setFilter] = useState<{
    [key: string]: string[];
  }>({});

  const hasOnePlaylistSelected = filter.playlist?.length === 1;

  // sorting
  const [sortKey, setSortKey] = useState<OrganizationVideoSortKey | null>(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState(PaginationConstants.ASCENDING);

  //queries
  const {
    data: filterData,
    isLoading: isLoadingFilters,
    isFetching: isFetchingFilters,
  } = useOrganizationVideoFiltersQuery();
  const filters = filterData?.filters || [];

  const {
    data,
    isLoading: isLoadingVideos,
    isFetching: isFetchingVideos,
  } = useOrganizationVideosQuery(
    {
      limit: DEFAULT_SIZE,
      page,
      search: searchTerm,
      filter,
      ...(!!sortKey && !!order ? { sortKey, order } : {}),
    },
    true,
    handleModalClose
  );
  const videos = data?.videos || [];
  const count = data?.count || 0;

  const handleFilterChange = (key: string, selectedOption: string[]) => {
    if (key === 'make') {
      setFilter(prevState => ({
        ...prevState,
        [key]: selectedOption,
        model: [],
      }));
      return;
    }
    setFilter(prevState => ({
      ...prevState,
      [key]: selectedOption,
    }));
  };

  const isPreparingData =
    (isLoadingVideos && !data) || (isLoadingFilters && !filterData);

  return (
    <FullScreenModal
      headerLeft={
        <LinkWrapper>
          <LinkBtn onClick={handleModalClose}>
            <MdClose size='18' />
            <span>Close</span>
          </LinkBtn>
        </LinkWrapper>
      }
      headerCenter={<Title>Available OEM Videos</Title>}
      headerRight={
        <Flex flexDirection='row' gap='8px'>
          {hasOnePlaylistSelected && (
            <Button
              variant='secondary'
              text='Duplicate Playlist'
              disabled={isPreparingData}
              onClick={() => setShowOrganizationDuplicatePlaylistModal(true)}
            />
          )}
          {isOrganizationAdmin && (
            <Button
              variant='primary'
              text='Add Video'
              disabled={isPreparingData}
              onClick={() => setShowOrganizationVideoFormModal(true)}
            />
          )}
        </Flex>
      }
    >
      <OrganizationVideosTable
        filters={filters}
        videos={videos}
        count={count}
        isPreparingData={isPreparingData}
        isFetchingVideos={isFetchingVideos}
        isFetchingFilters={isFetchingFilters}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setPage={setPage}
        page={page}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        setOrder={setOrder}
        order={order}
        handleModalClose={handleModalClose}
        onCopy={onCopy}
        filter={filter}
        handleFilterChange={handleFilterChange}
      />
      {showOrganizationVideoFormModal && (
        <ModalOrganizationVideoForm
          handleModalClose={() => setShowOrganizationVideoFormModal(false)}
        />
      )}
      {showOrganizationDuplicatePlaylistModal && (
        <ModalOrganizationDuplicatePlaylist
          filter={filter}
          handleModalClose={() => {
            setShowOrganizationDuplicatePlaylistModal(false);
          }}
        />
      )}
    </FullScreenModal>
  );
};
